import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { CompanyLogoModel } from 'src/models/v2/account';
import {
  DistributionGroupDto,
  GetAccountByidSubscriptionsQueryParams,
  GetDistributionGroupByidQueryParams,
  GetDistributionGroupQueryParams,
  GetMenuByidQueryParams,
  GetMenuItemByidQueryParams,
  GetMenuItemComponentByidQueryParams,
  GetMenuItemComponentQueryParams,
  GetMenuItemQueryParams,
  GetMenuQueryParams,
  GetMyAccountDetailsSubscriptionsQueryParams,
  GetNewsArticleByidQueryParams,
  GetProductLicenceScopeQueryParams,
  GetRoleByidPermissionsQueryParams,
  GetRoleQueryParams,
  GetUithemeByidQueryParams,
  MenuDto,
  MenuItemComponentDto,
  MenuItemDto,
  NewsArticleDto,
  PermissionDto,
  ProductLicenceScopeDto,
  RoleDto,
  SubscriptionDto,
  UithemeDto
} from 'src/models/account';
import { QueryProps, baseQueryFn } from 'src/queries/baseQueryFunctions';
import {
  getAccountSubscriptionsByAccountId,
  getMenuById,
  getMenuItemById,
  getMenuItemComponents,
  getMenuItemComponentById,
  getMenuItems,
  getMenus,
  getProductLicenceScopes,
  getRoleByIdPermissions,
  getRoles,
  getMyAccountDetailsSubscriptions,
  getDistributionGroups,
  getDistributionGroupById,
  getNewsArticleById,
  getNewsfeedArticleHtmlById,
  getCompanyLogoFromSubdomainV2,
  getAccountUIThemeById
} from 'src/services/account';

/**
 * Get Menus Query
 */
export const useGetMenusQuery = ({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<MenuDto[], GetMenuQueryParams>): UseQueryResult<MenuDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ? queryKeyOverride : ['menus', params],
    queryFn: () => baseQueryFn({ serviceFn: () => getMenus(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Menu by Id Query
 */
export const useGetMenuByIdQuery = (
  id: string,
  {
    enabled = true,
    initialData = [],
    onSuccess = undefined,
    refetchOnWindowFocus = false,
    customResponseModifier = undefined,
    queryKeyOverride,
    params = {}
  }: QueryProps<MenuDto, GetMenuByidQueryParams>
): UseQueryResult<MenuDto> => {
  return useQuery({
    queryKey: queryKeyOverride ? queryKeyOverride : ['menu', id, params],
    queryFn: () => baseQueryFn({ serviceFn: () => getMenuById(id, params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Menu Items Query
 */
export const useGetMenuItemsQuery = ({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<MenuItemDto[], GetMenuItemQueryParams>): UseQueryResult<MenuItemDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ? queryKeyOverride : ['menuItems', params],
    queryFn: () => baseQueryFn({ serviceFn: () => getMenuItems(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Menu Item Components Query
 */
export const useGetMenuItemComponentsQuery = ({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<MenuItemComponentDto[], GetMenuItemComponentQueryParams>): UseQueryResult<MenuItemComponentDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ? queryKeyOverride : ['menuItemComponents', params],
    queryFn: () => baseQueryFn({ serviceFn: () => getMenuItemComponents(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Menu Item by Id Query
 */
export const useGetMenuItemByIdQuery = (
  id: string,
  {
    enabled = true,
    initialData = {},
    onSuccess = undefined,
    refetchOnWindowFocus = false,
    customResponseModifier = undefined,
    queryKeyOverride,
    params = {}
  }: QueryProps<MenuItemDto, GetMenuItemByidQueryParams>
): UseQueryResult<MenuItemDto> => {
  return useQuery({
    queryKey: queryKeyOverride ? queryKeyOverride : ['menuItem', id, params],
    queryFn: () => baseQueryFn({ serviceFn: () => getMenuItemById(id, params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Menu Item Component by Id Query
 */
export const useGetMenuItemComponentByIdQuery = (
  id: string,
  {
    enabled = true,
    initialData = {},
    onSuccess = undefined,
    refetchOnWindowFocus = false,
    customResponseModifier = undefined,
    queryKeyOverride,
    params = {}
  }: QueryProps<MenuItemComponentDto, GetMenuItemComponentByidQueryParams>
): UseQueryResult<MenuItemComponentDto> => {
  return useQuery({
    queryKey: queryKeyOverride ? queryKeyOverride : ['menuItemComponent', id, params],
    queryFn: () => baseQueryFn({ serviceFn: () => getMenuItemComponentById(id, params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Account Subscriptions Query
 */
export const useGetAccountSubscriptionsQuery = (
  id: string,
  {
    enabled = true,
    initialData = [],
    onSuccess = undefined,
    refetchOnWindowFocus = false,
    customResponseModifier = undefined,
    queryKeyOverride,
    params = {}
  }: QueryProps<SubscriptionDto[], GetAccountByidSubscriptionsQueryParams>
): UseQueryResult<SubscriptionDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ? queryKeyOverride : ['accountSubscriptions', id],
    queryFn: () =>
      baseQueryFn({ serviceFn: () => getAccountSubscriptionsByAccountId(id, params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Roles Query
 */
export const useGetRolesQuery = (
  {
    enabled = true,
    initialData = [],
    onSuccess = undefined,
    refetchOnWindowFocus = false,
    customResponseModifier = undefined,
    queryKeyOverride,
    params = {}
  }: QueryProps<RoleDto[], GetRoleQueryParams>,
  tenantId?: string,
  partnerId?: string
): UseQueryResult<RoleDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ? queryKeyOverride : ['roles'],
    queryFn: () => baseQueryFn({ serviceFn: () => getRoles(params, tenantId, partnerId), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Role By Id Permissions Query
 */
export const useGetRoleByIdPermissionsQuery = (
  id: string,
  {
    enabled = true,
    initialData = [],
    onSuccess = undefined,
    refetchOnWindowFocus = false,
    customResponseModifier = undefined,
    queryKeyOverride,
    params = {}
  }: QueryProps<PermissionDto[], GetRoleByidPermissionsQueryParams>
): UseQueryResult<PermissionDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ? queryKeyOverride : ['rolePermissions', id, params],
    queryFn: () => baseQueryFn({ serviceFn: () => getRoleByIdPermissions(id, params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Product Licence Scopes
 */
export const useGetProductLicenceScopesQuery = ({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<ProductLicenceScopeDto[], GetProductLicenceScopeQueryParams>): UseQueryResult<
  ProductLicenceScopeDto[]
> => {
  return useQuery({
    queryKey: queryKeyOverride ? queryKeyOverride : ['productLicenceScopes', params],
    queryFn: () => baseQueryFn({ serviceFn: () => getProductLicenceScopes(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * My Account Details Subscriptions
 */
export const useGetMyAccountDetailsSubscriptionsQuery = ({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<SubscriptionDto[], GetMyAccountDetailsSubscriptionsQueryParams>): UseQueryResult<SubscriptionDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ? queryKeyOverride : ['MyAccountDetailsSubscriptions'],
    queryFn: () => baseQueryFn({ serviceFn: () => getMyAccountDetailsSubscriptions(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Distribution Groups
 */
export const useGetDistributionGroupsQuery = ({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<DistributionGroupDto[], GetDistributionGroupQueryParams>): UseQueryResult<DistributionGroupDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ? queryKeyOverride : ['distributionGroups'],
    queryFn: () => baseQueryFn({ serviceFn: () => getDistributionGroups(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Distribution Group by Id
 */
export const useGetDistributionGroupByIdQuery = (
  id: string,
  {
    enabled = true,
    initialData = {},
    onSuccess = undefined,
    refetchOnWindowFocus = false,
    customResponseModifier = undefined,
    queryKeyOverride,
    params = {}
  }: QueryProps<DistributionGroupDto, GetDistributionGroupByidQueryParams>
): UseQueryResult<DistributionGroupDto> => {
  return useQuery({
    queryKey: queryKeyOverride ? queryKeyOverride : ['distributionGroup', { id }],
    queryFn: () => baseQueryFn({ serviceFn: () => getDistributionGroupById(id, params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get News Article by Id Query
 */
export const useGetNewsArticleByIdQuery = (
  id: string,
  {
    enabled = true,
    initialData = {},
    onSuccess = undefined,
    refetchOnWindowFocus = false,
    customResponseModifier = undefined,
    queryKeyOverride,
    params = {}
  }: QueryProps<NewsArticleDto, GetNewsArticleByidQueryParams>
): UseQueryResult<NewsArticleDto> => {
  return useQuery({
    queryKey: queryKeyOverride ? queryKeyOverride : ['newsArticle', id],
    queryFn: () => baseQueryFn({ serviceFn: () => getNewsArticleById(id, params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get News Article HTML by Id Query
 */
export const useGetNewsArticleHtmlByIdQuery = (
  id: string,
  {
    enabled = true,
    initialData = '',
    onSuccess = undefined,
    refetchOnWindowFocus = false,
    customResponseModifier = undefined,
    queryKeyOverride
  }: QueryProps<string, {}>
): UseQueryResult<string> => {
  return useQuery({
    queryKey: queryKeyOverride ? queryKeyOverride : ['newsArticleHtml', id],
    queryFn: () => baseQueryFn({ serviceFn: () => getNewsfeedArticleHtmlById(id), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Company Logo by Subdomain Query
 */
export const useGetCompanyLogoBySubdomainQuery = (
  subdomain: string,
  {
    enabled = true,
    initialData = { logo: '', isDefaultPlatformLogo: false },
    onSuccess = undefined,
    onError = undefined,
    refetchOnWindowFocus = false,
    customResponseModifier = undefined,
    retry = true,
    queryKeyOverride
  }: QueryProps<CompanyLogoModel, string>
): UseQueryResult<CompanyLogoModel> => {
  return useQuery({
    queryKey: queryKeyOverride ? queryKeyOverride : ['companyLogoBySubdomain', subdomain],
    queryFn: () => baseQueryFn({ serviceFn: () => getCompanyLogoFromSubdomainV2({ subdomain }), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    retry,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) }),
    ...(typeof onError === 'function' && { onError: (err: any) => onError(err) })
  });
};

/**
 * Get UI Theme by Id Query
 */
export const useGetUIThemeByIdQuery = (
  id: string,
  {
    enabled = true,
    initialData = { logo: '', isDefaultPlatformLogo: false },
    onSuccess = undefined,
    onError = undefined,
    refetchOnWindowFocus = false,
    customResponseModifier = undefined,
    retry = true,
    queryKeyOverride,
    params = {}
  }: QueryProps<UithemeDto, GetUithemeByidQueryParams>
): UseQueryResult<UithemeDto> => {
  return useQuery({
    queryKey: queryKeyOverride ? queryKeyOverride : ['uiThemeById', id],
    queryFn: () => baseQueryFn({ serviceFn: () => getAccountUIThemeById(id, params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    retry,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) }),
    ...(typeof onError === 'function' && { onError: (err: any) => onError(err) })
  });
};
